import type { Alignment } from "./contracts.js";
import type { SpriteId } from "./contracts.js";
import type { ComponentSpriteId } from "./atlasComponents.js";
import type { TrackSample } from "./track.js";
import { rotate, type Rotation } from "./projection.js";

/** Any sprite the runtime can submit: frozen legacy ids plus Part B component ids. */
export type AtlasSpriteId = SpriteId | ComponentSpriteId;

/**
 * Draw order comes from the Part B precedence graph (precedence.ts, sceneOrder.ts). These fields feed the
 * renderer's `RenderSort` metadata only; station/trench group ids are metadata, never ordering inputs.
 */
export interface DepthItem {
  rotatedX: number;
  rotatedY: number;
  height: number;
  sortHeight?: number;
  occlusionGroup?: string;
  stableId: string;
}
export interface LayerVisibility {
  surface: boolean;
  underground: boolean;
}
export const visibleForAlignment = (
  alignment: Alignment,
  visibility: LayerVisibility,
): boolean =>
  alignment === "tunnel" || alignment === "openCut"
    ? visibility.underground
    : visibility.surface;
/** Ramps join tunnel and grade: visible when either layer is on, so a classification switch never hides them. */
export const visibleOnRamp = (visibility: LayerVisibility): boolean => visibility.surface || visibility.underground;
export type Bounds = [number, number, number, number];
export interface BoundedDepthItem extends DepthItem {
  bounds: Bounds;
}
export const boundsIntersect = (a: Bounds, b: Bounds): boolean =>
  a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
export class SpatialIndex<T extends { bounds: Bounds; stableId: string }> {
  private cells = new Map<string, T[]>();
  constructor(items: readonly T[], private cellSize = 32) {
    for (const item of items) {
      const minX = Math.floor(item.bounds[0] / cellSize),
        maxX = Math.floor(item.bounds[2] / cellSize),
        minY = Math.floor(item.bounds[1] / cellSize),
        maxY = Math.floor(item.bounds[3] / cellSize);
      for (let x = minX; x <= maxX; x += 1) {
        for (let y = minY; y <= maxY; y += 1) {
          const key = `${x}:${y}`, cell = this.cells.get(key) ?? [];
          cell.push(item);
          this.cells.set(key, cell);
        }
      }
    }
  }
  query(bounds: Bounds): T[] {
    const found = new Map<string, T>();
    for (
      let x = Math.floor(bounds[0] / this.cellSize);
      x <= Math.floor(bounds[2] / this.cellSize);
      x += 1
    ) {
      for (
        let y = Math.floor(bounds[1] / this.cellSize);
        y <= Math.floor(bounds[3] / this.cellSize);
        y += 1
      ) {
        for (
          const item of this.cells.get(`${x}:${y}`) ?? []
        ) if (boundsIntersect(item.bounds, bounds)) found.set(item.stableId, item);
      }
    }
    return [...found.values()];
  }
}
/**
 * Trace-facing precedence metadata, shared between fragments that carry the same values (most carry none). Trace
 * records expand it with the fragment and proxy ids (`<instanceId>`, `proxy:<instanceId>`).
 */
export interface DrawablePrecedence {
  crossingIds: readonly string[];
  /** Crossing and bed edges this fragment must draw after: `${before}>${reason}`. */
  requiredAfter: readonly string[];
  /** Edges into this fragment removed by cycle breaking: `${before}>${reason}`. */
  brokenAfter: readonly string[];
}
export interface SpriteDrawable extends BoundedDepthItem {
  spriteId?: AtlasSpriteId;
  frameIndex?: number;
  rect: [number, number, number, number];
  page?: number;
  anchor: [number, number];
  screen: [number, number];
  scale?: number;
  clip?: "back" | "front";
  precedence?: DrawablePrecedence;
  /** Runtime clip polygon (ramp faces), unit-screen offsets from the sprite's unit anchor. */
  clipPath?: readonly (readonly [number, number])[];
}
/** Paints drawables in the given (already ordered) sequence. */
export function paintScene(
  context: CanvasRenderingContext2D,
  atlas: CanvasImageSource | readonly CanvasImageSource[],
  drawables: readonly SpriteDrawable[],
  paintAfter?: (item: SpriteDrawable) => void,
): void {
  context.imageSmoothingEnabled = false;
  for (const item of drawables) {
    const [sx, sy, sw, sh] = item.rect,
      scale = item.scale ?? 1,
      dx = Math.round(item.screen[0] - item.anchor[0] * scale),
      dy = Math.round(item.screen[1] - item.anchor[1] * scale),
      source = Array.isArray(atlas) ? atlas[item.page ?? 0]! : atlas as CanvasImageSource;
    const previousAlpha = context.globalAlpha;
    if (item.spriteId === 'shadow') context.globalAlpha = .35;
    if (item.clip) {
      context.save();
      context.beginPath();
      const split = Math.round(item.screen[1]);
      context.rect(
        dx,
        item.clip === "back" ? dy : split,
        sw * scale,
        item.clip === "back"
          ? Math.max(0, split - dy)
          : Math.max(0, dy + sh * scale - split),
      );
      context.clip();
      context.drawImage(source, sx, sy, sw, sh, dx, dy, sw * scale, sh * scale);
      context.restore();
    } else {context.drawImage(
        source,
        sx,
        sy,
        sw,
        sh,
        dx,
        dy,
        sw * scale,
        sh * scale,
      );}
    context.globalAlpha = previousAlpha;
    paintAfter?.(item);
  }
}

export interface TrackStructure {
  spriteId: Extract<
    SpriteId,
    | "trestle"
    | "column"
    | "shadow"
    | "berm"
    | "trench/floor"
    | "trench/wall-back"
    | "trench/wall-front"
  >;
  sample: TrackSample;
  height: number;
  stableId: string;
  /** Metadata only (trace grouping); never an ordering input. */
  occlusionGroup?: string;
  lateralSide?: -1 | 1;
}
/** Legacy whole-wall sprite for a lateral side: the far wall uses the back art, the near wall the front art. */
export function trenchWallRole(
  heading: number,
  rotation: Rotation,
  lateralSide: -1 | 1,
): Pick<TrackStructure, 'spriteId'> {
  const lateral = rotate(
    [-Math.sin(heading), Math.cos(heading)],
    rotation,
  );
  return { spriteId: lateralSide * (lateral[0] + lateral[1]) < 0 ? 'trench/wall-back' : 'trench/wall-front' };
}
/** Lateral side (±1) that lies away from the camera at `rotation`; ties resolve to the left (−1). */
export const farLateralSide = (heading: number, rotation: Rotation): -1 | 1 => {
  const lateral = rotate([-Math.sin(heading), Math.cos(heading)], rotation);
  return lateral[0] + lateral[1] > 0 ? -1 : 1;
};
export function buildTrackStructures(
  samples: readonly TrackSample[],
): TrackStructure[] {
  const result: TrackStructure[] = [];
  samples.forEach((sample) => {
    const id = sample.physicalId ?? `${sample.pathId}:${sample.distance}`;
    if (sample.alignment === "tunnel" || sample.alignment === "openCut") {
      const occlusionGroup=`trench:${id}`;
      result.push(
        { spriteId: "trench/floor" as const, sample, height: -2.2, stableId: `trench-floor:${id}`, occlusionGroup },
        { spriteId: "trench/wall-back" as const, sample, height: -2, stableId: `trench-back:${id}`, occlusionGroup, lateralSide: -1 },
        { spriteId: "trench/wall-front" as const, sample, height: -2, stableId: `trench-front:${id}`, occlusionGroup, lateralSide: 1 },
      );
    } else if (sample.alignment === "embankment") {
      result.push({ spriteId: "berm" as const, sample, height: 0, stableId: `berm:${id}` });
    } else if (sample.alignment === "elevated") {
      result.push(
        { spriteId: "shadow" as const, sample, height: -0.1, stableId: `shadow:${id}` },
        { spriteId: "trestle" as const, sample, height: 3, stableId: `trestle:${id}` },
      );
      if (sample.column ?? Math.abs(sample.distance % 40) < 0.001) result.push({ spriteId: "column" as const, sample, height: 0, stableId: `column:${id}` });
    }
  });
  return result;
}
